/**
 * Survey Analysis Engine
 * ─────────────────────────
 * Analyzes survey responses and generates actionable recommendations for trainers:
 *  • exercises to avoid based on injuries
 *  • recommended training split
 *  • volume/intensity guidelines
 *  • equipment-appropriate exercises
 *  • schedule suggestions
 */

import type {
  SurveyTemplate,
  SurveyResponse,
  SurveyField,
  TrainingLevel,
  TrainingGoal,
  ProgressionModel,
  MovementPattern,
  MuscleGroup,
  LoadType,
} from '../types';
import { EXERCISE_DB } from '../data/exercises';
import { MUSCLE_GROUP_LABELS } from '../types';
import type { ExerciseDefinition } from '../types';

// ── Types ───────────────────────────────────

export interface AnalysisResult {
  clientName: string;
  clientAge: number | null;
  clientGender: string | null;
  clientWeight: number | null;
  clientHeight: number | null;
  bmi: number | null;
  bmiCategory: string | null;

  trainingLevel: TrainingLevel | null;
  goals: string[];
  availableDays: string[];
  sessionDuration: number | null;
  equipment: string[];
  avoidedEquipment: string[];
  injuries: string;
  currentActivity: string;
  preferences: string;
  additionalNotes: string;

  // Generated recommendations
  recommendations: Recommendation[];
  exercisesToAvoid: ExerciseWarning[];
  exercisesToRecommend: ExerciseRecommendation[];
  suggestedSplit: SplitSuggestion;
  volumeGuidelines: VolumeGuideline[];
  generalTips: string[];
}

export interface Recommendation {
  category: 'injury' | 'goal' | 'schedule' | 'equipment' | 'experience' | 'nutrition' | 'general';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  description: string;
}

export interface ExerciseWarning {
  exerciseId: string;
  exerciseName: string;
  reason: string;
  severity: 'avoid' | 'caution' | 'modify';
  alternative?: string;
}

export interface ExerciseRecommendation {
  exerciseId: string;
  exerciseName: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

export interface SplitSuggestion {
  name: string;
  description: string;
  days: { day: string; focus: string }[];
}

export interface VolumeGuideline {
  muscle: string;
  setsPerWeek: string;
  note: string;
}

export interface StarterPlanDraft {
  name: string;
  clientName: string;
  goal: TrainingGoal;
  trainingLevel: TrainingLevel;
  frequency: number;
  mesocycleLength: number;
  fatigueTolerance: number;
  progressionModel: ProgressionModel;
  sessionTemplates: { day: string; focus: string; exerciseIds: string[]; warmupExerciseIds: string[] }[];
  exercisePresets: Record<string, { sets: number; reps: string; load: number; loadType: LoadType; rest: number }>;
  rationale: string[];
}

// ── Injury Keyword → Body Part Mapping ──────

interface InjuryPattern {
  keywords: string[];
  bodyPart: string;
  avoidExerciseIds: string[];
  cautionExerciseIds: string[];
  alternatives: string[];
  tips: string[];
}

const INJURY_PATTERNS: InjuryPattern[] = [
  {
    keywords: ['kolano', 'kolana', 'acl', 'mcl', 'pcl', 'łąkotk', 'menisk', 'rzepk', 'staw kolanowy', 'kolank'],
    bodyPart: 'Kolano',
    avoidExerciseIds: [
      'pistol-squat', 'sissy-squat', 'jump-squat', 'walking-lunge', 'reverse-nordic',
    ],
    cautionExerciseIds: [
      'back-squat', 'front-squat', 'leg-extension', 'leg-press', 'hack-squat',
      'barbell-lunge', 'bulgarian-split-squat', 'single-leg-extension',
    ],
    alternatives: [
      'hip-thrust', 'glute-bridge', 'cable-pull-through', 'leg-curl',
      'seated-leg-curl', 'bodyweight-glute-bridge', 'clamshell',
    ],
    tips: [
      'Unikaj głębokich przysiadów z dużym obciążeniem do czasu ustąpienia bólu',
      'Prostowanie nóg może powodować ból - zaczynaj od małego zakresu ruchu',
      'Skup się na wzmocnieniu mięśni pośladkowych i hamstringów dla stabilizacji kolana',
      'Rozważ ćwiczenia na maszynie w zamkniętym łańcuchu kinetycznym',
    ],
  },
  {
    keywords: ['barkowy', 'bark', 'ramię', 'ramion', 'ramieniowy', 'rotator', 'stożek', 'obrotowy', 'impingement', 'rwa barkowa', 'barki'],
    bodyPart: 'Bark/Ramię',
    avoidExerciseIds: [
      'behind-neck-press', 'behind-neck-pulldown', 'upright-row',
      'handstand-push-up', 'overhead-squat',
    ],
    cautionExerciseIds: [
      'overhead-press', 'db-shoulder-press', 'arnold-press', 'lateral-raise',
      'barbell-bench-press', 'incline-barbell-press', 'dips', 'push-press',
      'smith-shoulder-press', 'z-press', 'viking-press',
    ],
    alternatives: [
      'landmine-press', 'cable-lateral-raise', 'machine-shoulder-press',
      'machine-chest-press', 'floor-press', 'face-pull', 'band-pull-apart',
    ],
    tips: [
      'Unikaj ćwiczeń za głowę - generują duże naprężenie na stożek rotatorów',
      'Włącz dużo pracy na rotatory zewnętrzne (face pull, band pull apart)',
      'Wyciskanie na maszynie lub z podłogi zmniejsza zakres ruchu problematycznego',
      'Zacznij od lekkiego ciężaru i stopniowo zwiększaj',
      'Rozgrzewka barkowa (YTWL) przed każdym treningiem górnej partii',
    ],
  },
  {
    keywords: ['plecy', 'kręgosłup', 'dysk', 'dyskopatia', 'lędźwi', 'odcinek lędźwiowy', 'dolne plecy', 'kręg', 'lordoza', 'kifoza', 'skolioza', 'rwa kulszowa', 'ischias'],
    bodyPart: 'Dolne plecy / Kręgosłup',
    avoidExerciseIds: [
      'conventional-deadlift', 'snatch-grip-deadlift', 'good-morning',
      'barbell-row', 'pendlay-row', 'russian-twist', 'decline-sit-up',
    ],
    cautionExerciseIds: [
      'back-squat', 'front-squat', 'romanian-deadlift', 'sumo-deadlift',
      'stiff-leg-deadlift', 'overhead-press', 't-bar-row', 'barbell-lunge',
      'hyperextension', 'ab-wheel',
    ],
    alternatives: [
      'trap-bar-deadlift', 'hip-thrust', 'leg-press', 'machine-row',
      'chest-supported-row', 'cable-row', 'plank', 'dead-bug',
      'bird-dog', 'pallof-press', 'glute-bridge',
    ],
    tips: [
      'Unikaj ciężkich ciągów ze sztangą z podłogi - rozważ trap bar lub ciąg z klocków',
      'Wiosłowanie z oparciem klatki piersiowej zmniejsza obciążenie pleców',
      'Skup się na stabilizacji core (plank, dead bug, pallof press)',
      'Unikaj ruchów rotacyjnych z obciążeniem do ustąpienia objawów',
      'Martwy ciąg rumuński tylko z lekkim ciężarem i pełną kontrolą',
    ],
  },
  {
    keywords: ['nadgarstek', 'nadgarst', 'ręka', 'dłoń', 'kanał nadgarstk', 'cieśń'],
    bodyPart: 'Nadgarstek/Dłoń',
    avoidExerciseIds: [
      'wrist-curl', 'reverse-wrist-curl', 'wrist-roller',
    ],
    cautionExerciseIds: [
      'barbell-bench-press', 'overhead-press', 'barbell-curl', 'front-squat',
      'push-up', 'handstand-push-up', 'farmers-walk',
    ],
    alternatives: [
      'machine-chest-press', 'machine-shoulder-press', 'cable-curl',
      'machine-row', 'leg-press', 'hack-squat',
    ],
    tips: [
      'Używaj uchwytów neutralnych gdy to możliwe (zmniejsza naprężenie nadgarstka)',
      'Maszyny i wyciągi są łagodniejsze dla nadgarstków niż wolne ciężary',
      'Rozważ paski na nadgarstki (wrist wraps) dla stabilizacji',
      'Unikaj hiperekstensji nadgarstka (np. front squat z pełnym chwytem)',
    ],
  },
  {
    keywords: ['łokieć', 'łokć', 'tendinit', 'łokieć tenisisty', 'łokieć golfisty', 'epicondyl', 'nadkłykieć'],
    bodyPart: 'Łokieć',
    avoidExerciseIds: [
      'skull-crusher', 'concentration-curl', 'preacher-curl',
    ],
    cautionExerciseIds: [
      'barbell-curl', 'barbell-bench-press', 'close-grip-bench',
      'overhead-tricep-ext', 'pull-up', 'chin-up', 'tricep-pushdown',
    ],
    alternatives: [
      'hammer-curl', 'cable-curl', 'machine-chest-press',
      'tricep-dip-machine', 'cable-fly', 'lat-pulldown',
    ],
    tips: [
      'Hammer curl (chwyt neutralny) jest najłagodniejszy dla łokcia',
      'Unikaj pełnego rozciągnięcia łokcia pod obciążeniem (skull crushers)',
      'Rozważ użycie EZ barki zamiast prostej sztangi - mniejsze naprężenie',
      'Stopniowo zwiększaj objętość - nagły wzrost obciążenia pogarsza stan',
    ],
  },
  {
    keywords: ['biodro', 'biodr', 'hip', 'pachwina', 'przywodziciel', 'FAI', 'labrum'],
    bodyPart: 'Biodro',
    avoidExerciseIds: [
      'sumo-deadlift', 'hip-adduction', 'lateral-lunge', 'curtsy-lunge',
    ],
    cautionExerciseIds: [
      'back-squat', 'front-squat', 'bulgarian-split-squat', 'hip-thrust',
      'barbell-lunge', 'walking-lunge', 'pistol-squat',
    ],
    alternatives: [
      'leg-press', 'hack-squat', 'leg-extension', 'leg-curl',
      'glute-bridge', 'cable-pull-through', 'step-up',
    ],
    tips: [
      'Unikaj głębokich przysiadów z szerokim rozkrokiem (sumo)',
      'Leg press z umiarkowanym zakresem ruchu jest bezpieczniejszy',
      'Wzmacnianie pośladków i core pomoże stabilizować biodro',
      'Rozgrzewka z gumami oporowymi (clamshell, monster walk) przed treningiem',
    ],
  },
  {
    keywords: ['kostka', 'stopa', 'stóp', 'achilles', 'ścięgno achillesa', 'skoczny', 'piętowy', 'podeszwowy', 'fascja'],
    bodyPart: 'Stopa/Kostka',
    avoidExerciseIds: [
      'jump-squat', 'standing-calf-raise', 'donkey-calf-raise',
    ],
    cautionExerciseIds: [
      'back-squat', 'walking-lunge', 'step-up', 'barbell-calf-raise',
      'single-leg-calf-raise', 'leg-press-calf-raise',
    ],
    alternatives: [
      'leg-press', 'seated-calf-raise', 'leg-extension', 'leg-curl',
      'hip-thrust', 'machine-row',
    ],
    tips: [
      'Unikaj ćwiczeń plyometrycznych i skoków do ustąpienia bólu',
      'Wspięcia na palce wykonuj tylko bez bólu - zacznij od siedzących',
      'Przysiadaj w butach z podwyższoną piętą lub używaj podkładki',
      'Wzmacniaj tibialis anterior (odwrotne wspięcia) dla profilaktyki',
    ],
  },
  {
    keywords: ['szyja', 'szczęka', 'odcinek szyjny', 'kark', 'whiplash'],
    bodyPart: 'Szyja/Kark',
    avoidExerciseIds: [
      'behind-neck-press', 'behind-neck-pulldown', 'barbell-shrug',
    ],
    cautionExerciseIds: [
      'overhead-press', 'back-squat', 'upright-row',
    ],
    alternatives: [
      'machine-shoulder-press', 'lat-pulldown', 'front-squat',
      'safety-bar-squat', 'face-pull',
    ],
    tips: [
      'Unikaj ciężaru bezpośrednio na szyi (high bar squat → safety bar)',
      'Wyciskanie za głowę wykluczone - duże naprężenie na odcinek szyjny',
      'Wzmacniaj mięśnie stabilizujące łopatki i górne plecy',
    ],
  },
];

// ── Helper: extract answer ──────────────────

function getAnswerByLabel(
  survey: SurveyTemplate,
  response: SurveyResponse,
  labelIncludes: string
): string | string[] | number | boolean | undefined {
  const normalizeText = (v: string) =>
    v
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const needles = labelIncludes
    .split('|')
    .map((s) => normalizeText(s))
    .filter(Boolean);

  const answersRaw: any = response.answers as any;

  const field = survey.fields.find(
    (f) => {
      const label = normalizeText(f.label);
      return needles.some((n) => label.includes(n));
    }
  );

  // 1) Current format: answers is an object keyed by fieldId
  if (field && answersRaw && typeof answersRaw === 'object' && !Array.isArray(answersRaw)) {
    if (Object.prototype.hasOwnProperty.call(answersRaw, field.id)) {
      return answersRaw[field.id];
    }
  }

  // 2) Legacy array format: [{ fieldId, value }] or similar
  if (field && Array.isArray(answersRaw)) {
    const hit = answersRaw.find((a: any) =>
      a?.fieldId === field.id || a?.id === field.id || a?.questionId === field.id
    );
    if (hit && Object.prototype.hasOwnProperty.call(hit, 'value')) {
      return hit.value;
    }
  }

  // 3) Legacy object keyed by question label/name
  if (answersRaw && typeof answersRaw === 'object' && !Array.isArray(answersRaw)) {
    for (const [k, v] of Object.entries(answersRaw)) {
      const key = normalizeText(k);
      if (needles.some((n) => key.includes(n))) {
        return v as any;
      }
    }
  }

  // 4) Legacy array entries containing text labels
  if (Array.isArray(answersRaw)) {
    const hit = answersRaw.find((a: any) => {
      const label = normalizeText(String(a?.label || a?.question || a?.name || ''));
      return needles.some((n) => label.includes(n));
    });
    if (hit) {
      if (Object.prototype.hasOwnProperty.call(hit, 'value')) return hit.value;
      if (Object.prototype.hasOwnProperty.call(hit, 'answer')) return hit.answer;
    }
  }

  return undefined;
}

function getStringAnswer(survey: SurveyTemplate, response: SurveyResponse, label: string): string {
  const val = getAnswerByLabel(survey, response, label);
  if (val === undefined || val === null) return '';
  if (Array.isArray(val)) return val.join(', ');
  return String(val);
}

function getArrayAnswer(survey: SurveyTemplate, response: SurveyResponse, label: string): string[] {
  const val = getAnswerByLabel(survey, response, label);
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val) {
    if (val.includes(',') || val.includes(';')) {
      return val.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
    }
    return [val];
  }
  return [];
}

function getNumberAnswer(survey: SurveyTemplate, response: SurveyResponse, label: string): number | null {
  const val = getAnswerByLabel(survey, response, label);
  if (typeof val === 'number') return val;
  if (typeof val === 'string') { const n = parseFloat(val); return isNaN(n) ? null : n; }
  return null;
}

// ── Main Analysis Function ──────────────────

export function analyzeSurveyResponse(
  survey: SurveyTemplate,
  response: SurveyResponse
): AnalysisResult {
  const clientName = getStringAnswer(survey, response, 'imię|imie|name|full name') || response.respondentName;
  const clientAge = getNumberAnswer(survey, response, 'wiek|age');
  const clientGender = getStringAnswer(survey, response, 'płeć|plec|gender|sex');
  const clientWeight = getNumberAnswer(survey, response, 'waga|weight|bodyweight|body weight');
  const clientHeight = getNumberAnswer(survey, response, 'wzrost|height');

  // BMI
  let bmi: number | null = null;
  let bmiCategory: string | null = null;
  if (clientWeight && clientHeight) {
    bmi = Math.round((clientWeight / ((clientHeight / 100) ** 2)) * 10) / 10;
    if (bmi < 18.5) bmiCategory = 'Niedowaga';
    else if (bmi < 25) bmiCategory = 'Norma';
    else if (bmi < 30) bmiCategory = 'Nadwaga';
    else bmiCategory = 'Otyłość';
  }

  const levelStr = getStringAnswer(survey, response, 'doświadczenie|doswiadczenie|experience|level');
  const goals = getArrayAnswer(survey, response, 'cel|goal|goals|target');
  const availableDays = getArrayAnswer(survey, response, 'dni|day|days|dostepne dni|available days');
  const sessionDurationRaw = getNumberAnswer(survey, response, 'czas|minut|duration|session time') ?? getNumberAnswer(survey, response, 'sesj|session');
  const sessionDuration = sessionDurationRaw;
  const equipment = getArrayAnswer(survey, response, 'sprzęt|sprzet|equipment|gear');
  const injuries = getStringAnswer(survey, response, 'kontuzj|ograniczen|injur|pain|rehab') || getStringAnswer(survey, response, 'problem');
  const currentActivity = getStringAnswer(survey, response, 'aktualn|obecn|activity|current training|current activity');
  const preferences = getStringAnswer(survey, response, 'ulubion|nielub|prefer|dislike|like');
  const additionalNotes = getStringAnswer(survey, response, 'dodatkow|uwag|notes|comment|additional');

  // Map level
  let trainingLevel: TrainingLevel | null = null;
  if (levelStr.includes('Początkujący') || levelStr.includes('< 1')) trainingLevel = 'beginner';
  else if (levelStr.includes('Średnio') || levelStr.includes('1–3')) trainingLevel = 'intermediate';
  else if (levelStr.includes('Zaawansowany') || levelStr.includes('3–7')) trainingLevel = 'advanced';
  else if (levelStr.includes('Ekspert') || levelStr.includes('7+')) trainingLevel = 'elite';

  // ── Analyze injuries ──
  const exercisesToAvoid: ExerciseWarning[] = [];
  const recommendations: Recommendation[] = [];
  const injuryLower = injuries.toLowerCase();
  const preferencesLower = preferences.toLowerCase();

  for (const pattern of INJURY_PATTERNS) {
    const matched = pattern.keywords.some((kw) => injuryLower.includes(kw));
    if (!matched) continue;

    // Add recommendation
    recommendations.push({
      category: 'injury',
      severity: 'critical',
      title: `Kontuzja: ${pattern.bodyPart}`,
      description: pattern.tips.join(' '),
    });

    // Mark exercises to avoid
    for (const exId of pattern.avoidExerciseIds) {
      const ex = EXERCISE_DB.find((e) => e.id === exId);
      if (ex) {
        const altEx = pattern.alternatives[0] ? EXERCISE_DB.find((e) => e.id === pattern.alternatives[0]) : null;
        exercisesToAvoid.push({
          exerciseId: exId,
          exerciseName: ex.name,
          reason: `Problematyczne przy kontuzji: ${pattern.bodyPart}`,
          severity: 'avoid',
          alternative: altEx?.name,
        });
      }
    }

    // Mark caution exercises
    for (const exId of pattern.cautionExerciseIds) {
      const ex = EXERCISE_DB.find((e) => e.id === exId);
      if (ex) {
        exercisesToAvoid.push({
          exerciseId: exId,
          exerciseName: ex.name,
          reason: `Ostrożność przy kontuzji: ${pattern.bodyPart} - zredukuj ciężar/zakres ruchu`,
          severity: 'caution',
        });
      }
    }
  }

  // ── Equipment-based filtering ──
  // Also parse preferences for exercises to avoid
  if (preferencesLower) {
    // Check if any exercise name in the DB is mentioned as disliked
    const dislikePatterns = ['nie lubię', 'nie chcę', 'unikaj', 'nie lubi', 'niechcian', 'bez ', 'omijaj', 'nie chce'];
    const hasDislikePrefix = dislikePatterns.some((p) => preferencesLower.includes(p));
    if (hasDislikePrefix) {
      for (const ex of EXERCISE_DB) {
        const exNameLower = ex.name.toLowerCase();
        if (preferencesLower.includes(exNameLower)) {
          // Check if not already in the avoid list
          if (!exercisesToAvoid.some((a) => a.exerciseId === ex.id)) {
            exercisesToAvoid.push({
              exerciseId: ex.id,
              exerciseName: ex.name,
              reason: 'Klient zgłosił to ćwiczenie jako niechciane/nielubiane',
              severity: 'avoid',
            });
          }
        }
      }
    }
  }

  const hasFullGym = equipment.some((e) => e.toLowerCase().includes('pełna') || e.toLowerCase().includes('komercyjna'));
  const hasHomeGym = equipment.some((e) => e.toLowerCase().includes('domowa'));
  const hasDumbbells = equipment.some((e) => e.toLowerCase().includes('hantl'));
  const hasBarbell = equipment.some((e) => e.toLowerCase().includes('sztanga'));
  const hasBands = equipment.some((e) => e.toLowerCase().includes('gum'));
  const hasMachines = equipment.some((e) => e.toLowerCase().includes('maszyn'));
  const hasCable = equipment.some((e) => e.toLowerCase().includes('wyciąg'));
  const bodyweightOnly = equipment.some((e) => e.toLowerCase().includes('brak') || e.toLowerCase().includes('masa ciała'));

  // ── Avoided equipment ──
  const avoidedEquipment = getArrayAnswer(survey, response, 'sprzęt którego chcesz unikać|sprzet ktorego chcesz unikac|avoid equipment|equipment to avoid')
    .concat(getArrayAnswer(survey, response, 'unikać|unikac|avoid'));
  const avoidedEquipLower = avoidedEquipment.map((e) => e.toLowerCase());
  const avoidsBarbell = avoidedEquipLower.some((e) => e.includes('sztang'));
  const avoidsDumbbells = avoidedEquipLower.some((e) => e.includes('hantl'));
  const avoidsKettlebell = avoidedEquipLower.some((e) => e.includes('kettlebell'));
  const avoidsBands = avoidedEquipLower.some((e) => e.includes('gum'));
  const avoidsMachines = avoidedEquipLower.some((e) => e.includes('maszyn'));
  const avoidsCable = avoidedEquipLower.some((e) => e.includes('wyciąg') || e.includes('kabel'));

  // Mark exercises using avoided equipment as caution
  if (avoidsBarbell || avoidsDumbbells || avoidsKettlebell || avoidsBands || avoidsMachines || avoidsCable) {
    for (const ex of EXERCISE_DB) {
      const nameL = ex.name.toLowerCase();
      let reason = '';
      if (avoidsBarbell && (nameL.includes('sztang') || nameL.includes('barbell') || ex.id.includes('barbell'))) {
        reason = 'Klient chce unikać ćwiczeń ze sztangą';
      } else if (avoidsDumbbells && (nameL.includes('hantl') || nameL.includes('dumbbell') || ex.id.includes('db-'))) {
        reason = 'Klient chce unikać ćwiczeń z hantlami';
      } else if (avoidsKettlebell && (nameL.includes('kettlebell') || ex.id.includes('kettlebell') || ex.id.includes('kb-'))) {
        reason = 'Klient chce unikać kettlebelli';
      } else if (avoidsMachines && (nameL.includes('maszyn') || nameL.includes('machine') || ex.id.includes('machine'))) {
        reason = 'Klient chce unikać maszyn';
      } else if (avoidsCable && (nameL.includes('wyciąg') || nameL.includes('cable') || ex.id.includes('cable'))) {
        reason = 'Klient chce unikać wyciągu/kabla';
      }
      if (reason && !exercisesToAvoid.some((a) => a.exerciseId === ex.id)) {
        exercisesToAvoid.push({
          exerciseId: ex.id,
          exerciseName: ex.name,
          reason,
          severity: 'caution',
        });
      }
    }
    if (avoidedEquipment.length > 0 && !avoidedEquipment.some((e) => e.toLowerCase().includes('żaden'))) {
      recommendations.push({
        category: 'equipment',
        severity: 'info',
        title: 'Sprzęt do unikania',
        description: `Klient chce unikać: ${avoidedEquipment.join(', ')}. Uwzględnij to przy doborze ćwiczeń.`,
      });
    }
  }

  if (bodyweightOnly) {
    recommendations.push({
      category: 'equipment',
      severity: 'warning',
      title: 'Tylko ćwiczenia z masą ciała',
      description: 'Klient nie ma dostępu do sprzętu. Plan powinien zawierać pompki, podciągnięcia, przysiady, wykroki, planki i ich warianty. Progresję realizuj przez zwiększanie reps, tempo, i warianty trudniejsze.',
    });
  }

  if (!hasFullGym && !hasMachines) {
    recommendations.push({
      category: 'equipment',
      severity: 'info',
      title: 'Brak maszyn',
      description: 'Klient nie ma dostępu do maszyn. Używaj wariantów z wolnymi ciężarami, wyciągami lub gumami zamiast ćwiczeń maszynowych.',
    });
  }

  // Based on goals
  const wantsHypertrophy = goals.some((g) => g.toLowerCase().includes('mięśn') || g.toLowerCase().includes('masa'));
  const wantsStrength = goals.some((g) => g.toLowerCase().includes('siła'));
  const wantsFatLoss = goals.some((g) => g.toLowerCase().includes('redukcj') || g.toLowerCase().includes('tłuszcz'));
  const wantsGeneral = goals.some((g) => g.toLowerCase().includes('ogóln') || g.toLowerCase().includes('sprawnoś'));
  const wantsEndurance = goals.some((g) => g.toLowerCase().includes('wytrzym'));
  const wantsHealth = goals.some((g) => g.toLowerCase().includes('zdrow'));

  if (wantsHypertrophy) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Cel: Hipertrofia',
      description: 'Zalecaj 10-20 serii na grupę mięśniową/tydzień. RPE 7-9. Tempo kontrolowane (3-1-2-0). Progresja: dodawaj serie co tydzień (2-3 serie). Deload co 4-6 tygodni.',
    });
  }

  if (wantsStrength) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Cel: Siła',
      description: 'Skupić się na zakresie 3-6 powtórzeń w ćwiczeniach złożonych. RPE 8-9.5. Dłuższe przerwy (3-5 min). Progresja liniowa ciężaru.',
    });
  }

  if (wantsFatLoss) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Cel: Redukcja',
      description: 'Priorytet: zachowanie masy mięśniowej. Treningi siłowe 3-4x/tydz. Ćwiczenia złożone w pierwszej kolejności. Krótsze przerwy (60-90s). Rozważ superset/circuit training.',
    });
    recommendations.push({
      category: 'nutrition',
      severity: 'warning',
      title: 'Deficyt kaloryczny',
      description: 'Redukcja wymaga deficytu kalorycznego. Zalecany deficyt 300-500 kcal/dzień. Białko minimum 1.8-2.2g/kg masy ciała dla ochrony mięśni.',
    });
  }

  if (wantsEndurance) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Cel: Wytrzymałość',
      description: 'Wyższy zakres powtórzeń (12-20+). Krótsze przerwy (30-60s). Superset i giant set. Rozważ circuit training. Dołącz cardio.',
    });
  }

  if (wantsHealth) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Cel: Poprawa zdrowia',
      description: 'Priorytet: umiarkowane obciążenie z pełnym zakresem ruchu. Włącz mobilność, rozciąganie, ćwiczenia równowagi. 2-3 treningi siłowe + 2-3 sesje cardio/tydz.',
    });
  }

  // ── Age-based recommendations ──
  if (clientAge !== null) {
    if (clientAge < 16) {
      recommendations.push({
        category: 'general',
        severity: 'warning',
        title: 'Młody wiek (< 16 lat)',
        description: 'Unikaj maksymalnych ciężarów (1-3RM). Skup się na technice i umiarkowanych ciężarach. Więcej powtórzeń (8-15). Nadzór nad wykonywaną techniką. Objętość treningowa powinna być zredukowana o ~30%.',
      });
    }
    if (clientAge >= 16 && clientAge <= 17) {
      recommendations.push({
        category: 'general',
        severity: 'info',
        title: 'Wiek nastoletni (16-17 lat)',
        description: 'Możliwy trening siłowy, ale z naciskiem na technikę. Unikaj 1RM. Umiarkowana objętość (zredukowana ~15%). Dłuższa rozgrzewka. Monitoring wzrostu i rozwoju.',
      });
    }
    if (clientAge >= 18 && clientAge <= 30) {
      recommendations.push({
        category: 'general',
        severity: 'info',
        title: 'Optymalny wiek treningowy (18-30)',
        description: 'Pełen zakres metod treningowych. Możliwy wysoki wolumen i intensywność. Szybka regeneracja - można trenować częściej.',
      });
    }
    if (clientAge > 30 && clientAge <= 50) {
      recommendations.push({
        category: 'general',
        severity: 'info',
        title: 'Wiek 31-50',
        description: 'Dłuższa rozgrzewka. Regeneracja nieco wydłużona. Kontroluj stawry - rozważ więcej pracy mobilizacyjnej. Objętość standardowa.',
      });
    }
    if (clientAge > 50 && clientAge <= 65) {
      recommendations.push({
        category: 'general',
        severity: 'warning',
        title: 'Wiek 50-65',
        description: 'Objętość zredukowana o ~20%. Dłuższa rozgrzewka (10-15 min). Unikaj ekstremalnych obciążeń. Więcej pracy stabilizacyjnej i mobilności. Preferuj maszyny i kontrolowane ruchy. Przerwy między seriami: 2-3 min.',
      });
    }
    if (clientAge > 65) {
      recommendations.push({
        category: 'general',
        severity: 'warning',
        title: 'Wiek 65+',
        description: 'Objętość zredukowana o ~35%. Priorytet: utrzymanie masy mięśniowej i siły funkcjonalnej. Unikaj nagłych ruchów. Więcej pracy równowagowej. Ćwiczenia na maszynie bezpieczniejsze niż wolne ciężary. Minimum 3 min przerwy.',
      });
    }
  }

  // ── Experience-based ──
  if (trainingLevel === 'beginner') {
    recommendations.push({
      category: 'experience',
      severity: 'info',
      title: 'Początkujący - Fundament techniczny',
      description: 'Rozpocznij od ćwiczeń maszynowych i prostych ruchów złożonych. Full-body 3x/tydz. lub Upper/Lower. Naucz techniki przede wszystkim. 3 serie po 8-12 powtórzeń na ćwiczenie. Ciężar dobierz na RPE 6-7.',
    });
  }
  if (trainingLevel === 'intermediate') {
    recommendations.push({
      category: 'experience',
      severity: 'info',
      title: 'Średniozaawansowany - Progresja objętości',
      description: 'Można zwiększyć podział treningowy (Upper/Lower, PPL). Progresja: zwiększaj serie i ciężar stopniowo. RPE 7-8.5. Periodyzacja falująca (DUP) działa dobrze.',
    });
  }
  if (trainingLevel === 'advanced' || trainingLevel === 'elite') {
    recommendations.push({
      category: 'experience',
      severity: 'info',
      title: 'Zaawansowany - Specjalizacja',
      description: 'Periodyzacja blokowa lub falująca. Skupienie na słabych punktach. Wyższa objętość (16-25 serii/mięsień/tydz). Deload co 3-4 tygodnie. Techniki intensyfikujące (drop sety, rest-pause, myo-reps).',
    });
  }

  // ── NEW RULES (6 cases) ──────────

  // 1. Goal: Strength
  if (goals.some(g => g.toLowerCase().includes('siła') || g.toLowerCase().includes('strenght'))) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Fokus na siłę (Strength)',
      description: 'Dla budowania siły zaleca się wykonywanie ćwiczeń wielostawowych w zakresie 3-6 powtórzeń z odpowiednio długimi przerwami (3-5 minut).',
    });
  }

  // 2. Goal: Endurance / Kondycja
  if (goals.some(g => g.toLowerCase().includes('kondycj') || g.toLowerCase().includes('wytrzyma'))) {
    recommendations.push({
      category: 'goal',
      severity: 'info',
      title: 'Fokus na kondycję / wytrzymałość',
      description: 'Dla poprawy wytrzymałości mięśniowej celuj w zakresy 15+ powtórzeń i krótsze przerwy (30-60 sekund).',
    });
  }

  // 3. Activity: Sedentary / Siedzący tryb życia
  if (currentActivity && (currentActivity.toLowerCase().includes('siedz') || currentActivity.toLowerCase().includes('brak'))) {
    recommendations.push({
      category: 'general',
      severity: 'info',
      title: 'Siedzący tryb życia',
      description: 'Zaleca się zwiększenie spontanicznej aktywności fizycznej (NEAT), np. poprzez regularne spacery i unikanie długiego siedzenia bez przerw.',
    });
  }

  // 4. Age: Seniors (>50)
  if (clientAge && clientAge >= 50) {
    recommendations.push({
      category: 'general',
      severity: 'info',
      title: 'Trening w starszym wieku (50+)',
      description: 'Kluczowa jest dłuższa rozgrzewka stawów, dobór ćwiczeń bezpiecznych dla kręgosłupa i kolan oraz upewnienie się o odpowiednio długiej regeneracji.',
    });
  }

  // 5. Time Limit: Low volume of days (1-2 days)
  if (availableDays.length > 0 && availableDays.length <= 2) {
    recommendations.push({
      category: 'schedule',
      severity: 'info',
      title: 'Ograniczony czas treningowy',
      description: 'Przy 1-2 dniach treningowych w tygodniu zależy nam na maksymalnej efektywności. Trening Full Body Workout (FBW) bazujący na ćwiczeniach wielostawowych sprawdzi się najlepiej.',
    });
  }

  // 6. BMI > 30: Low impact cardio
  if (bmi && bmi >= 30) {
    recommendations.push({
      category: 'general',
      severity: 'warning',
      title: 'Wyższe BMI - Odciążenie stawów',
      description: 'Przy wyższej wadze (BMI > 30) aby chronić stawy (szczególnie kolana), preferuj aktywności typu low-impact: maszyny, rower, orbitrek czy pływanie. Unikaj ćwiczeń skocznych (plyometrii).',
    });
  }

  // ── Schedule / Split suggestion ──
  const numDays = availableDays.length || 3;
  const suggestedSplit = generateSplitSuggestion(numDays, trainingLevel, goals);

  // ── Volume guidelines ──
  const volumeGuidelines = generateVolumeGuidelines(trainingLevel, goals, clientAge);

  // ── General tips ──
  const generalTips: string[] = [];

  if (sessionDuration && sessionDuration <= 45) {
    generalTips.push('Krótkie sesje (≤45 min) - skup się na superseriach i ćwiczeniach złożonych. Ogranicz izolację do minimum.');
  }
  if (sessionDuration && sessionDuration >= 90) {
    generalTips.push('Dłuższe sesje (≥90 min) - możesz dodać więcej izolacji i pracy akcesoryjnej. Pilnuj nawodnienia.');
  }

  generalTips.push('Rozgrzewka: 5-10 min ogólna + serie rozgrzewkowe ćwiczeń złożonych.');
  generalTips.push('Progresję zapisuj w dzienniku treningowym - ciężar, serie, powtórzenia, RPE.');

  if (bmi && bmi > 30) {
    generalTips.push('BMI wskazuje na otyłość - uważaj na stawy (kolana, biodra). Preferuj maszyny i ćwiczenia z mniejszym obciążeniem osiowym.');
  }

  // Preferences as tips
  if (preferences) {
    generalTips.push(`Preferencje klienta: "${preferences}" - uwzględnij to przy doborze ćwiczeń.`);
  }
  if (additionalNotes) {
    generalTips.push(`Dodatkowe uwagi: "${additionalNotes}"`);
  }

  return {
    clientName,
    clientAge,
    clientGender,
    clientWeight,
    clientHeight,
    bmi,
    bmiCategory,
    trainingLevel,
    goals,
    availableDays,
    sessionDuration,
    equipment,
    avoidedEquipment,
    injuries,
    currentActivity,
    preferences,
    additionalNotes,
    recommendations,
    exercisesToAvoid,
    exercisesToRecommend: [],
    suggestedSplit,
    volumeGuidelines,
    generalTips,
  };
}

// ── Split Generator ─────────────────────────

function generateSplitSuggestion(
  daysPerWeek: number,
  level: TrainingLevel | null,
  goals: string[]
): SplitSuggestion {
  if (daysPerWeek <= 2) {
    return {
      name: 'Full Body 2×',
      description: 'Trening całego ciała 2 razy w tygodniu - optymalne dla 2 dostępnych dni',
      days: [
        { day: 'Dzień 1', focus: 'Full Body A (nacisk na przysiad + wyciskanie)' },
        { day: 'Dzień 2', focus: 'Full Body B (nacisk na ciąg + ciągnięcie)' },
      ],
    };
  }
  if (daysPerWeek === 3) {
    if (level === 'beginner') {
      return {
        name: 'Full Body 3×',
        description: 'Trening całego ciała 3× w tygodniu - idealny dla początkujących',
        days: [
          { day: 'Dzień 1', focus: 'Full Body A (przysiad, wyciskanie, wiosłowanie)' },
          { day: 'Dzień 2', focus: 'Full Body B (martwy ciąg, wciskanie nad głowę, ciągnięcie)' },
          { day: 'Dzień 3', focus: 'Full Body C (warianty lżejsze, izolacja, core)' },
        ],
      };
    }
    return {
      name: 'Push / Pull / Legs',
      description: 'Podział PPL 1× w tygodniu - dobry dla 3 dni',
      days: [
        { day: 'Dzień 1', focus: 'Push (klatka, barki, triceps)' },
        { day: 'Dzień 2', focus: 'Pull (plecy, biceps, tylne barki)' },
        { day: 'Dzień 3', focus: 'Legs (nogi, pośladki, core)' },
      ],
    };
  }
  if (daysPerWeek === 4) {
    return {
      name: 'Upper / Lower 2×',
      description: 'Podział Upper/Lower powtórzony 2×/tydz. - optymalny dla 4 dni',
      days: [
        { day: 'Dzień 1', focus: 'Upper A (wyciskanie + wiosłowanie - ciężko)' },
        { day: 'Dzień 2', focus: 'Lower A (przysiad + martwy ciąg - ciężko)' },
        { day: 'Dzień 3', focus: 'Upper B (wyciskanie + ciągnięcie - lekko/objętość)' },
        { day: 'Dzień 4', focus: 'Lower B (nogi + pośladki - lekko/objętość)' },
      ],
    };
  }
  if (daysPerWeek === 5) {
    return {
      name: 'Upper / Lower / Push / Pull / Legs',
      description: 'Hybrydowy podział 5-dniowy',
      days: [
        { day: 'Dzień 1', focus: 'Upper (siła - wyciskanie + wiosłowanie ciężko)' },
        { day: 'Dzień 2', focus: 'Lower (siła - przysiad, ciąg ciężko)' },
        { day: 'Dzień 3', focus: 'Push (objętość - klatka, barki, triceps)' },
        { day: 'Dzień 4', focus: 'Pull (objętość - plecy, biceps)' },
        { day: 'Dzień 5', focus: 'Legs (objętość - nogi, pośladki, core)' },
      ],
    };
  }
  // 6-7 days
  return {
    name: 'Push / Pull / Legs 2×',
    description: 'Podział PPL powtórzony 2× w tygodniu - wysoka częstotliwość',
    days: [
      { day: 'Dzień 1', focus: 'Push A (klatka nacisk, barki, triceps - ciężko)' },
      { day: 'Dzień 2', focus: 'Pull A (plecy, biceps - ciężko)' },
      { day: 'Dzień 3', focus: 'Legs A (przysiad, hamstringi - ciężko)' },
      { day: 'Dzień 4', focus: 'Push B (klatka objem., barki objem. - lekko)' },
      { day: 'Dzień 5', focus: 'Pull B (plecy objem., biceps - lekko)' },
      { day: 'Dzień 6', focus: 'Legs B (nogi objem., pośladki, core)' },
    ],
  };
}

// ── Volume Guidelines ───────────────────────

function generateVolumeGuidelines(
  level: TrainingLevel | null,
  goals: string[],
  age: number | null
): VolumeGuideline[] {
  const wantsHypertrophy = goals.some((g) => g.toLowerCase().includes('mięśn') || g.toLowerCase().includes('masa'));
  const isBeginnerOrNull = !level || level === 'beginner';
  const isAdvanced = level === 'advanced' || level === 'elite';

  // Base ranges by level
  let lowMin = isBeginnerOrNull ? 6 : isAdvanced ? 12 : 10;
  let lowMax = isBeginnerOrNull ? 10 : isAdvanced ? 16 : 14;
  let midMin = isBeginnerOrNull ? 8 : isAdvanced ? 14 : 10;
  let midMax = isBeginnerOrNull ? 12 : isAdvanced ? 20 : 16;
  let highMin = isBeginnerOrNull ? 10 : isAdvanced ? 16 : 12;
  let highMax = isBeginnerOrNull ? 14 : isAdvanced ? 25 : 18;

  // ── Age-based volume modifiers ──
  let ageNote = '';
  if (age !== null) {
    if (age < 16) {
      // Youth: lower volume, focus on technique
      const factor = 0.7;
      lowMin = Math.round(lowMin * factor); lowMax = Math.round(lowMax * factor);
      midMin = Math.round(midMin * factor); midMax = Math.round(midMax * factor);
      highMin = Math.round(highMin * factor); highMax = Math.round(highMax * factor);
      ageNote = ' (zredukowane - młody wiek, nacisk na technikę)';
    } else if (age >= 16 && age <= 17) {
      // Teens: slightly reduced
      const factor = 0.85;
      lowMin = Math.round(lowMin * factor); lowMax = Math.round(lowMax * factor);
      midMin = Math.round(midMin * factor); midMax = Math.round(midMax * factor);
      highMin = Math.round(highMin * factor); highMax = Math.round(highMax * factor);
      ageNote = ' (lekko zredukowane - wiek nastoletni)';
    } else if (age >= 50 && age < 65) {
      // 50+: reduce by ~15-20%, longer recovery
      const factor = 0.8;
      lowMin = Math.round(lowMin * factor); lowMax = Math.round(lowMax * factor);
      midMin = Math.round(midMin * factor); midMax = Math.round(midMax * factor);
      highMin = Math.round(highMin * factor); highMax = Math.round(highMax * factor);
      ageNote = ' (zredukowane - wiek 50+, dłuższa regeneracja)';
    } else if (age >= 65) {
      // 65+: significant reduction, prioritize maintenance
      const factor = 0.65;
      lowMin = Math.round(lowMin * factor); lowMax = Math.round(lowMax * factor);
      midMin = Math.round(midMin * factor); midMax = Math.round(midMax * factor);
      highMin = Math.round(highMin * factor); highMax = Math.round(highMax * factor);
      ageNote = ' (znacznie zredukowane - wiek 65+, priorytet: utrzymanie masy mięśniowej)';
    }
  }

  const low = `${lowMin}-${lowMax}`;
  const mid = `${midMin}-${midMax}`;
  const high = `${highMin}-${highMax}`;

  return [
    { muscle: 'Klatka piersiowa', setsPerWeek: wantsHypertrophy ? high : mid, note: 'Wyciskania + rozpiętki' + ageNote },
    { muscle: 'Plecy (najszersze)', setsPerWeek: wantsHypertrophy ? high : mid, note: 'Ciągnięcia pionowe + poziome' + ageNote },
    { muscle: 'Barki (boczne)', setsPerWeek: wantsHypertrophy ? high : mid, note: 'Unoszenia boczne + wyciskania' + ageNote },
    { muscle: 'Biceps', setsPerWeek: wantsHypertrophy ? mid : low, note: 'Uginania + praca pośrednia z ciągnięć' + ageNote },
    { muscle: 'Triceps', setsPerWeek: wantsHypertrophy ? mid : low, note: 'Prostowania + praca pośrednia z wycisku' + ageNote },
    { muscle: 'Czworogłowy', setsPerWeek: wantsHypertrophy ? high : mid, note: 'Przysiady + prostowania nóg' + ageNote },
    { muscle: 'Dwugłowy (hamstringi)', setsPerWeek: wantsHypertrophy ? mid : low, note: 'Martwy ciąg + uginania nóg' + ageNote },
    { muscle: 'Pośladki', setsPerWeek: wantsHypertrophy ? mid : low, note: 'Hip thrust + przysiady + wykroki' + ageNote },
    { muscle: 'Łydki', setsPerWeek: low, note: 'Wspięcia stojąc + siedząc' + ageNote },
    { muscle: 'Brzuch / Core', setsPerWeek: low, note: 'Plank, dead bug, cable crunch, ab wheel' + ageNote },
  ];
}

// ── Starter Plan Draft Generator ──────────────────

const DAY_ORDER: Record<string, number> = {
  'poniedziałek': 1,
  'wtorek': 2,
  'środa': 3,
  'czwartek': 4,
  'piątek': 5,
  'sobota': 6,
  'niedziela': 7,
};

function normalizeAndSortDays(days: string[]): string[] {
  const unique = Array.from(new Set(days.map((d) => d.trim()).filter(Boolean)));
  return unique.sort((a, b) => {
    const aKey = DAY_ORDER[a.toLowerCase()] ?? 999;
    const bKey = DAY_ORDER[b.toLowerCase()] ?? 999;
    return aKey - bKey;
  });
}

function getEquipmentFlags(analysis: AnalysisResult) {
  const lower = analysis.equipment.map((e) => e.toLowerCase());
  const avoided = analysis.avoidedEquipment.map((e) => e.toLowerCase());

  return {
    hasAny: lower.length > 0,
    hasFullGym: lower.some((e) => e.includes('pełna') || e.includes('komercyjna')),
    hasHomeGym: lower.some((e) => e.includes('domowa')),
    hasBarbell: lower.some((e) => e.includes('sztang')),
    hasDumbbells: lower.some((e) => e.includes('hantl')),
    hasKettlebell: lower.some((e) => e.includes('kettlebell')),
    hasMachines: lower.some((e) => e.includes('maszyn')),
    hasCable: lower.some((e) => e.includes('wyciąg') || e.includes('kabel')),
    bodyweightOnly: lower.some((e) => e.includes('brak') || e.includes('masa ciała')),
    avoidBarbell: avoided.some((e) => e.includes('sztang')),
    avoidDumbbells: avoided.some((e) => e.includes('hantl')),
    avoidKettlebell: avoided.some((e) => e.includes('kettlebell')),
    avoidMachines: avoided.some((e) => e.includes('maszyn')),
    avoidCable: avoided.some((e) => e.includes('wyciąg') || e.includes('kabel')),
  };
}

function matchesAny(text: string, patterns: string[]): boolean {
  return patterns.some((p) => text.includes(p));
}

function isExerciseAllowedForStarter(ex: ExerciseDefinition, analysis: AnalysisResult): boolean {
  const flags = getEquipmentFlags(analysis);
  const id = ex.id.toLowerCase();
  const name = ex.name.toLowerCase();

  const avoidedIds = new Set(
    analysis.exercisesToAvoid
      .filter((a) => a.severity === 'avoid' || a.severity === 'caution')
      .map((a) => a.exerciseId)
  );
  if (avoidedIds.has(ex.id)) return false;

  if (flags.bodyweightOnly) {
    const bodyweightHints = ['push-up', 'pull-up', 'chin-up', 'plank', 'hanging', 'pallof', 'deska'];
    return matchesAny(id, bodyweightHints) || matchesAny(name, ['podciągan', 'pomp', 'deska', 'zwisie', 'pallof']);
  }

  if (flags.hasFullGym) return true;

  const needsBarbell = matchesAny(id, ['barbell', 'squat', 'deadlift', 'good-morning', 'zercher', 'smith'])
    || matchesAny(name, ['sztang']);
  const needsDumbbells = matchesAny(id, ['db-', 'dumbbell']) || matchesAny(name, ['hantl']);
  const needsKettlebell = matchesAny(id, ['kettlebell', 'kb-']) || matchesAny(name, ['kettlebell']);
  const needsMachine = matchesAny(id, ['machine', 'leg-press', 'hack-squat', 'pec-deck']) || matchesAny(name, ['maszyn', 'prasa', 'peck-deck', 'suwnic']);
  const needsCable = matchesAny(id, ['cable', 'pulldown', 'pull-through']) || matchesAny(name, ['wyciąg', 'linki']);

  if (flags.avoidBarbell && needsBarbell) return false;
  if (flags.avoidDumbbells && needsDumbbells) return false;
  if (flags.avoidKettlebell && needsKettlebell) return false;
  if (flags.avoidMachines && needsMachine) return false;
  if (flags.avoidCable && needsCable) return false;

  if (needsBarbell && !(flags.hasBarbell || flags.hasHomeGym)) return false;
  if (needsDumbbells && !(flags.hasDumbbells || flags.hasHomeGym)) return false;
  if (needsKettlebell && !flags.hasKettlebell) return false;
  if (needsMachine && !flags.hasMachines) return false;
  if (needsCable && !flags.hasCable) return false;

  if (!flags.hasAny) return true;
  return true;
}

function pickStarterExercisesForFocus(focus: string, analysis: AnalysisResult): string[] {
  const focusL = focus.toLowerCase();
  const baseCount = analysis.sessionDuration && analysis.sessionDuration <= 45 ? 4
    : analysis.sessionDuration && analysis.sessionDuration >= 75 ? 5
      : 4;

  const targets: string[] = [];
  if (focusL.includes('push')) {
    targets.push('barbell-bench-press', 'incline-db-press', 'db-shoulder-press', 'cable-row', 'lateral-raise', 'tricep-pushdown', 'face-pull', 'plank');
  } else if (focusL.includes('pull')) {
    targets.push('lat-pulldown', 'cable-row', 'chest-supported-row', 'farmers-walk', 'rear-delt-fly', 'face-pull', 'hammer-curl', 'pallof-press');
  } else if (focusL.includes('leg')) {
    targets.push('back-squat', 'romanian-deadlift', 'leg-press', 'leg-curl', 'standing-calf-raise', 'plank', 'farmers-walk');
  } else if (focusL.includes('upper')) {
    targets.push('barbell-bench-press', 'chest-supported-row', 'lat-pulldown', 'db-shoulder-press', 'lateral-raise', 'rear-delt-fly', 'hammer-curl', 'plank');
  } else if (focusL.includes('lower')) {
    targets.push('back-squat', 'hip-thrust', 'romanian-deadlift', 'farmers-walk', 'leg-curl', 'standing-calf-raise', 'plank');
  } else {
    targets.push('goblet-squat', 'barbell-bench-press', 'lat-pulldown', 'farmers-walk', 'hip-thrust', 'face-pull', 'plank');
  }

  const chosen: string[] = [];
  for (const id of targets) {
    const ex = EXERCISE_DB.find((e) => e.id === id);
    if (!ex) continue;
    if (!isExerciseAllowedForStarter(ex, analysis)) continue;
    chosen.push(ex.id);
    if (chosen.length >= baseCount) break;
  }

  if (chosen.length < Math.max(3, baseCount - 1)) {
    const fallbackByPattern = EXERCISE_DB
      .filter((e) => {
        if (!isExerciseAllowedForStarter(e, analysis)) return false;
        if (focusL.includes('push')) return e.movementPattern === 'horizontal_push' || e.movementPattern === 'vertical_push' || e.primaryMuscle === 'triceps' || e.primaryMuscle === 'side_delts';
        if (focusL.includes('pull')) return e.movementPattern === 'horizontal_pull' || e.movementPattern === 'vertical_pull' || e.primaryMuscle === 'biceps' || e.primaryMuscle === 'rear_delts';
        if (focusL.includes('leg') || focusL.includes('lower')) return e.movementPattern === 'squat' || e.movementPattern === 'hinge' || e.movementPattern === 'isolation_lower' || e.primaryMuscle === 'calves';
        return e.movementPattern === 'squat' || e.movementPattern === 'hinge' || e.movementPattern === 'horizontal_push' || e.movementPattern === 'horizontal_pull' || e.movementPattern === 'core';
      })
      .sort((a, b) => {
        const aScore = (a.tag === 'compound' ? 2 : 0) + (a.movementPattern === 'core' ? -1 : 0);
        const bScore = (b.tag === 'compound' ? 2 : 0) + (b.movementPattern === 'core' ? -1 : 0);
        return bScore - aScore;
      });

    for (const ex of fallbackByPattern) {
      if (chosen.includes(ex.id)) continue;
      chosen.push(ex.id);
      if (chosen.length >= baseCount) break;
    }
  }

  return chosen.slice(0, baseCount);
}

function findAllowedExerciseIdByPattern(pattern: MovementPattern, analysis: AnalysisResult, avoidIds: Set<string>): string | null {
  const pool = EXERCISE_DB
    .filter((e) => e.movementPattern === pattern)
    .sort((a, b) => {
      const aScore = (a.tag === 'compound' ? 2 : 0) + (a.defaultRPE <= 8 ? 1 : 0);
      const bScore = (b.tag === 'compound' ? 2 : 0) + (b.defaultRPE <= 8 ? 1 : 0);
      return bScore - aScore;
    });

  for (const ex of pool) {
    if (avoidIds.has(ex.id)) continue;
    if (!isExerciseAllowedForStarter(ex, analysis)) continue;
    return ex.id;
  }
  return null;
}

function optimizeSessionTemplatesForQuality(
  templates: { day: string; focus: string; exerciseIds: string[] }[],
  analysis: AnalysisResult,
): { day: string; focus: string; exerciseIds: string[] }[] {
  const next = templates.map((t) => ({ ...t, exerciseIds: [...t.exerciseIds] }));
  const avoidIds = new Set(
    analysis.exercisesToAvoid
      .filter((a) => a.severity === 'avoid' || a.severity === 'caution')
      .map((a) => a.exerciseId)
  );

  const getPatternSet = () => {
    const set = new Set<MovementPattern>();
    for (const s of next) {
      for (const id of s.exerciseIds) {
        const ex = EXERCISE_DB.find((e) => e.id === id);
        if (ex) set.add(ex.movementPattern);
      }
    }
    return set;
  };

  // Ensure essential coverage + core/carry
  const required: MovementPattern[] = [
    'squat', 'hinge', 'horizontal_push', 'vertical_push', 'horizontal_pull', 'vertical_pull', 'core', 'carry',
  ];
  let covered = getPatternSet();
  for (const pattern of required) {
    if (covered.has(pattern)) continue;
    const candidateId = findAllowedExerciseIdByPattern(pattern, analysis, avoidIds);
    if (!candidateId) continue;
    const target = next.find((s) => s.exerciseIds.length < 6) || next[0];
    if (!target.exerciseIds.includes(candidateId)) {
      target.exerciseIds.push(candidateId);
    }
    covered = getPatternSet();
  }

  // Push/pull balancing target ~0.9-1.15
  const countPushPull = () => {
    let push = 0;
    let pull = 0;
    for (const s of next) {
      for (const id of s.exerciseIds) {
        const ex = EXERCISE_DB.find((e) => e.id === id);
        if (!ex) continue;
        if (ex.movementPattern === 'horizontal_push' || ex.movementPattern === 'vertical_push') push += ex.defaultSets;
        if (ex.movementPattern === 'horizontal_pull' || ex.movementPattern === 'vertical_pull') pull += ex.defaultSets;
      }
    }
    return { push, pull, ratio: push / Math.max(pull, 1) };
  };

  let pp = countPushPull();
  if (pp.ratio > 1.2) {
    const pullId = findAllowedExerciseIdByPattern('horizontal_pull', analysis, avoidIds)
      || findAllowedExerciseIdByPattern('vertical_pull', analysis, avoidIds);
    if (pullId) {
      const target = next.find((s) => s.exerciseIds.length < 6) || next[0];
      if (!target.exerciseIds.includes(pullId)) target.exerciseIds.push(pullId);
    }
  } else if (pp.ratio < 0.8) {
    const pushId = findAllowedExerciseIdByPattern('horizontal_push', analysis, avoidIds)
      || findAllowedExerciseIdByPattern('vertical_push', analysis, avoidIds);
    if (pushId) {
      const target = next.find((s) => s.exerciseIds.length < 6) || next[0];
      if (!target.exerciseIds.includes(pushId)) target.exerciseIds.push(pushId);
    }
  }

  // Avoid very high per-session volume drivers by capping to 5 exercises/session
  for (const s of next) {
    if (s.exerciseIds.length > 5) {
      s.exerciseIds = s.exerciseIds.slice(0, 5);
    }
  }

  // Re-check required movement patterns after cap (carry/core were sometimes trimmed).
  covered = getPatternSet();
  for (const pattern of required) {
    if (covered.has(pattern)) continue;
    const candidateId = findAllowedExerciseIdByPattern(pattern, analysis, avoidIds);
    if (!candidateId) continue;

    const target = next.find((s) => s.exerciseIds.length < 5) || next[next.length - 1];
    if (!target.exerciseIds.includes(candidateId)) {
      if (target.exerciseIds.length >= 5) {
        target.exerciseIds[target.exerciseIds.length - 1] = candidateId;
      } else {
        target.exerciseIds.push(candidateId);
      }
    }
    covered = getPatternSet();
  }

  return next;
}

function mapGoalFromAnalysis(analysis: AnalysisResult): TrainingGoal {
  const lowerGoals = analysis.goals.map((g) => g.toLowerCase());
  if (lowerGoals.some((g) => g.includes('siła'))) return 'strength';
  if (lowerGoals.some((g) => g.includes('wytrzyma'))) return 'endurance';
  if (lowerGoals.some((g) => g.includes('masa') || g.includes('mięśn') || g.includes('hipertrof'))) return 'hypertrophy';
  return 'general';
}

function mapProgression(level: TrainingLevel): ProgressionModel {
  if (level === 'beginner') return 'linear';
  if (level === 'intermediate') return 'undulating';
  if (level === 'advanced') return 'block';
  return 'autoregulated';
}

function parseVolumeRangeMidpoint(value: string): number {
  const m = value.match(/(\d+)\s*-\s*(\d+)/);
  if (m) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    return Math.round((a + b) / 2);
  }
  const single = parseInt(value, 10);
  return Number.isNaN(single) ? 10 : single;
}

function goalDefaultRepScheme(goal: TrainingGoal, isCompound: boolean): { reps: string; load: number; rest: number } {
  if (goal === 'strength') {
    return isCompound
      ? { reps: '4-6', load: 8.5, rest: 180 }
      : { reps: '6-10', load: 8, rest: 90 };
  }
  if (goal === 'endurance') {
    return isCompound
      ? { reps: '10-15', load: 7, rest: 90 }
      : { reps: '12-20', load: 7, rest: 60 };
  }
  if (goal === 'general') {
    return isCompound
      ? { reps: '6-10', load: 7.5, rest: 120 }
      : { reps: '10-15', load: 7, rest: 75 };
  }
  // hypertrophy default
  return isCompound
    ? { reps: '6-10', load: 8, rest: 120 }
    : { reps: '10-15', load: 7.5, rest: 75 };
}

function buildExercisePresetsForDraft(
  analysis: AnalysisResult,
  sessionTemplates: { day: string; focus: string; exerciseIds: string[]; warmupExerciseIds: string[] }[],
  goal: TrainingGoal,
  level: TrainingLevel,
): Record<string, { sets: number; reps: string; load: number; loadType: LoadType; rest: number }> {
  const labelToMuscle = Object.entries(MUSCLE_GROUP_LABELS).reduce((acc, [key, label]) => {
    acc[label.toLowerCase()] = key as MuscleGroup;
    return acc;
  }, {} as Record<string, MuscleGroup>);

  const targetWeeklySets: Partial<Record<MuscleGroup, number>> = {};
  for (const vg of analysis.volumeGuidelines) {
    const key = labelToMuscle[vg.muscle.toLowerCase()];
    if (!key) continue;
    targetWeeklySets[key] = parseVolumeRangeMidpoint(vg.setsPerWeek);
  }

  const selectedExerciseIds = Array.from(new Set(sessionTemplates.flatMap((s) => s.exerciseIds)));

  // Count real weekly occurrences (same exercise can appear in multiple sessions).
  const exerciseOccurrences: Record<string, number> = {};
  for (const s of sessionTemplates) {
    for (const exId of s.exerciseIds) {
      exerciseOccurrences[exId] = (exerciseOccurrences[exId] || 0) + 1;
    }
  }

  const primaryMuscleOccurrences: Partial<Record<MuscleGroup, number>> = {};
  for (const exId of selectedExerciseIds) {
    const ex = EXERCISE_DB.find((e) => e.id === exId);
    if (!ex) continue;
    const occ = exerciseOccurrences[exId] || 1;
    primaryMuscleOccurrences[ex.primaryMuscle] = (primaryMuscleOccurrences[ex.primaryMuscle] || 0) + occ;
  }

  const preset: Record<string, { sets: number; reps: string; load: number; loadType: LoadType; rest: number }> = {};
  const hasCritical = analysis.recommendations.some((r) => r.severity === 'critical');

  for (const exId of selectedExerciseIds) {
    const ex = EXERCISE_DB.find((e) => e.id === exId);
    if (!ex) continue;

    const isCompound = ex.tag === 'compound';
    const defaults = goalDefaultRepScheme(goal, isCompound);

    const targetForMuscle = targetWeeklySets[ex.primaryMuscle];
    const muscleOccurrences = Math.max(primaryMuscleOccurrences[ex.primaryMuscle] || 1, 1);
    const targetPerExercisePerSession = targetForMuscle
      ? Math.max(1, Math.round(targetForMuscle / muscleOccurrences))
      : undefined;

    let sets = ex.movementPattern === 'core' || ex.movementPattern === 'carry'
      ? 2
      : targetPerExercisePerSession ?? (isCompound ? 2 : 2);

    // Keep starter plans recoverable and inside sensible MEV→MRV corridor.
    const maxSets = goal === 'strength' ? 3 : 4;
    const minSets = ex.movementPattern === 'core' || ex.movementPattern === 'carry' ? 2 : 2;
    sets = Math.max(minSets, Math.min(maxSets, sets));

    if (level === 'beginner') {
      sets = Math.max(minSets, sets - 1);
    }

    let load = defaults.load;
    let rest = defaults.rest;

    if ((analysis.clientAge ?? 0) >= 50 || hasCritical) {
      load = Math.max(6.5, load - 0.5);
      rest += 15;
    }

    preset[exId] = {
      sets,
      reps: defaults.reps,
      load,
      loadType: 'rpe',
      rest,
    };
  }

  return preset;
}

function pickWarmupExercisesForSession(
  focus: string,
  exerciseIds: string[],
  analysis: AnalysisResult,
): string[] {
  const focusL = focus.toLowerCase();
  const injuriesL = analysis.injuries.toLowerCase();
  const mainPatterns = new Set(
    exerciseIds
      .map((id) => EXERCISE_DB.find((e) => e.id === id)?.movementPattern)
      .filter((v): v is MovementPattern => !!v)
  );

  const candidates: string[] = ['wu-jumping-jacks', 'wu-worlds-greatest-stretch'];

  if (focusL.includes('push') || focusL.includes('upper')) {
    candidates.push('wu-arm-circles', 'wu-band-pull-apart', 'wu-ytwl');
  }
  if (focusL.includes('pull')) {
    candidates.push('wu-band-face-pull', 'wu-cat-cow', 'wu-bird-dog');
  }
  if (focusL.includes('leg') || focusL.includes('lower')) {
    candidates.push('wu-leg-swings-front', 'wu-bodyweight-squat', 'wu-glute-bridge', 'wu-band-monster-walk');
  }

  if (mainPatterns.has('squat')) candidates.push('wu-bodyweight-squat', 'wu-leg-swings-front');
  if (mainPatterns.has('hinge')) candidates.push('wu-glute-bridge', 'wu-band-clamshell');
  if (mainPatterns.has('horizontal_pull') || mainPatterns.has('vertical_pull')) candidates.push('wu-band-face-pull');
  if (mainPatterns.has('vertical_push')) candidates.push('wu-band-dislocate', 'wu-ytwl');

  if (injuriesL.includes('bark')) candidates.push('wu-ytwl', 'wu-band-pull-apart');
  if (injuriesL.includes('plecy') || injuriesL.includes('kręgosłup') || injuriesL.includes('lędźwi')) {
    candidates.push('wu-bird-dog', 'wu-dead-bug', 'wu-cat-cow');
  }
  if (injuriesL.includes('kolan') || injuriesL.includes('biodr')) {
    candidates.push('wu-band-clamshell', 'wu-leg-swings-lateral');
  }

  const warmupSet = new Set(
    EXERCISE_DB.filter((e) => e.tag === 'warmup').map((e) => e.id)
  );

  const unique = Array.from(new Set(candidates)).filter((id) => warmupSet.has(id));
  return unique.slice(0, 4);
}

export function buildStarterPlanFromAnalysis(analysis: AnalysisResult): StarterPlanDraft {
  const level = analysis.trainingLevel ?? 'intermediate';
  const goal = mapGoalFromAnalysis(analysis);
  const sortedDays = normalizeAndSortDays(analysis.availableDays);
  const suggestedDays = analysis.suggestedSplit.days;
  const criticalCount = analysis.recommendations.filter((r) => r.severity === 'critical').length;

  let frequency = sortedDays.length;
  if (frequency === 0) frequency = Math.max(2, Math.min(4, suggestedDays.length || 3));
  frequency = Math.max(2, Math.min(6, frequency));

  let mesocycleLength = level === 'advanced' || level === 'elite' ? 5 : 4;
  if (criticalCount > 0 || (analysis.clientAge ?? 0) >= 60) {
    mesocycleLength = 4;
  }

  let fatigueTolerance = level === 'beginner' ? 5 : level === 'intermediate' ? 6 : 7;
  if (criticalCount > 0) fatigueTolerance = Math.max(4, fatigueTolerance - 1);
  if ((analysis.clientAge ?? 0) >= 50) fatigueTolerance = Math.max(4, fatigueTolerance - 1);

  const rawSessionTemplates = Array.from({ length: frequency }).map((_, idx) => {
    const splitDay = suggestedDays[idx % suggestedDays.length] || { day: `Dzień ${idx + 1}`, focus: 'Ogólne' };
    const day = sortedDays[idx] || splitDay.day;
    const focus = splitDay.focus;
    return {
      day,
      focus,
      exerciseIds: pickStarterExercisesForFocus(focus, analysis),
      warmupExerciseIds: [],
    };
  });

  const optimizedTemplates = optimizeSessionTemplatesForQuality(rawSessionTemplates, analysis);
  const sessionTemplates = optimizedTemplates.map((t) => ({
    ...t,
    warmupExerciseIds: pickWarmupExercisesForSession(t.focus, t.exerciseIds, analysis),
  }));
  const exercisePresets = buildExercisePresetsForDraft(analysis, sessionTemplates, goal, level);

  const rationale: string[] = [
    `Cel planu: ${goal}`,
    `Poziom: ${level}`,
    `Częstotliwość: ${frequency} sesji/tydzień`,
  ];
  if (criticalCount > 0) rationale.push('Wykryto istotne ograniczenia - obniżona tolerancja zmęczenia i krótszy mezocykl.');
  if (analysis.availableDays.length > 0) rationale.push('Dni treningowe zostały ustawione na podstawie ankiety klienta.');
  if (analysis.exercisesToAvoid.length > 0) rationale.push('Generator pominął ćwiczenia oznaczone jako do unikania/ostrożności.');
  rationale.push('Generator wyrównuje balans push/pull oraz pilnuje pokrycia kluczowych wzorców ruchowych.');
  rationale.push('Objętość (serie) jest ustawiana warunkowo pod wytyczne MEV/MRV z analizy ankiety.');
  rationale.push('Każda sesja dostaje sensowny blok rozgrzewki pod fokus i wzorce ruchu.');
  rationale.push(`Logika progresji ustawiona jako ${mapProgression(level)} dla poziomu ${level}.`);

  return {
    name: `Plan startowy - ${analysis.clientName}`,
    clientName: analysis.clientName,
    goal,
    trainingLevel: level,
    frequency,
    mesocycleLength,
    fatigueTolerance,
    progressionModel: mapProgression(level),
    sessionTemplates,
    exercisePresets,
    rationale,
  };
}
